import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import {
  signIn,
  confirmSignIn,
  signOut as amplifySignOut,
  fetchAuthSession,
} from 'aws-amplify/auth'
import { apiCallable } from '@/lib/api'
import { setTimezone, getTimezonePreference, getTimezone } from '@/lib/dateTime'
import i18n from '@/lib/i18n'
import type { User } from '@/types'

interface AuthResult {
  success: boolean
  error?: string
}

interface AuthContextValue {
  user: User | null
  loading: boolean
  error: string | null
  /** Step 1: request a one-time login code be emailed to the user. */
  sendLoginCode: (email: string) => Promise<AuthResult>
  /** Step 2: verify the emailed code and complete sign-in. */
  verifyCode: (code: string) => Promise<AuthResult>
  signOut: () => Promise<void>
  /** Alias for `signOut` (backward-compatible with existing call sites). */
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Resolve the current Cognito session into an app `User`: reads the ID token,
   * calls `setupUser` to load groups/timezone/language, and reconciles the
   * timezone/language preferences with the server profile.
   */
  const bootstrapSession = useCallback(async (): Promise<User | null> => {
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken
    if (!idToken) {
      setUser(null)
      return null
    }

    const payload = idToken.payload as Record<string, unknown>
    const uid = (payload.sub as string) || ''
    const email = (payload.email as string) || null

    try {
      // setupUser derives the email from the token server-side now; we still
      // send `userEmail` for compatibility (harmless).
      const setupUser = apiCallable('setupUser')
      const result = await setupUser({ userEmail: email })
      const data = result.data as {
        success: boolean
        groups?: string[]
        timezone?: string | null
        language?: string | null
        isSuperAdmin?: boolean
      }

      // Sync timezone from server profile on first login / when no local preference is set
      if (data.timezone && getTimezonePreference() === 'browser') {
        setTimezone(data.timezone)
      }
      // If user has a local timezone set but server doesn't have one yet, push it up
      if (!data.timezone && getTimezonePreference() !== 'browser') {
        const updateTz = apiCallable('updateUserTimezone')
        updateTz({ timezone: getTimezone() }).catch((err) => {
          console.error('Failed to sync timezone to profile:', err)
        })
      }

      // Sync language: server profile takes precedence if set
      const localLang = localStorage.getItem('edge_manager_language')
      if (data.language && data.language !== i18n.language) {
        i18n.changeLanguage(data.language)
      } else if (!data.language && localLang) {
        // Push local language to server if server doesn't have one yet
        const updateLang = apiCallable('updateUserLanguage')
        updateLang({ language: i18n.language }).catch((err) => {
          console.error('Failed to sync language to profile:', err)
        })
      }

      const nextUser: User = {
        uid,
        email,
        groups: data.groups || [],
        isSuperAdmin: data.isSuperAdmin || false,
      }
      setUser(nextUser)
      setError(null)
      return nextUser
    } catch (err) {
      console.error('Failed to setup user:', err)
      const fallbackUser: User = { uid, email, groups: [] }
      setUser(fallbackUser)
      setError(null)
      return fallbackUser
    }
  }, [])

  // Restore session on load
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        await bootstrapSession()
      } catch (err) {
        // Not signed in (or session unavailable) — treat as logged out.
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bootstrapSession])

  const sendLoginCode = useCallback(async (email: string): Promise<AuthResult> => {
    setError(null)
    try {
      await signIn({
        username: email,
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'EMAIL_OTP',
        },
      })
      return { success: true }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send login code'
      setError(message)
      return { success: false, error: message }
    }
  }, [])

  const verifyCode = useCallback(
    async (code: string): Promise<AuthResult> => {
      setError(null)
      try {
        const { isSignedIn } = await confirmSignIn({ challengeResponse: code })
        if (isSignedIn) {
          await bootstrapSession()
          return { success: true }
        }
        const message = 'Additional verification required'
        setError(message)
        return { success: false, error: message }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to verify code'
        setError(message)
        return { success: false, error: message }
      }
    },
    [bootstrapSession]
  )

  const signOut = useCallback(async () => {
    try {
      await amplifySignOut()
    } catch (err) {
      console.error('Sign out error:', err)
    }
    setUser(null)
    setError(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{ user, loading, error, sendLoginCode, verifyCode, signOut, logout: signOut }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
